import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  setLoading,
  unsetLoading,
} from "../../../../store/loading/loading.slice";
import {
  alertActive,
  alertError,
  alertSuccess,
} from "../../../../components/alert/swal";
import ModalCustom from "../../../../components/modal/ModalCustom";
import { Grid } from "@mui/material";
import {
  ButtonOutlined,
  ButtonPrimary,
} from "../../../../components/button/ButtonCustoms";
import InputTextField from "../../../../components/input/inputTextField";
import { advisorTypeApi } from "../../../../api/advisor-type/advisor-type.api";
import { NavigateFunction, useNavigate } from "react-router-dom";

type ModalFormConsultationProps = {
  isOpenModal: boolean;
  setIsOpenModal: (isOpen: boolean) => void;
  action: string;
  onFetch?: () => void;
  data?: any;
};

const ModalFormConsultation: React.FC<ModalFormConsultationProps> = ({
  isOpenModal,
  setIsOpenModal,
  action,
  onFetch,
  data,
}) => {
  const navigate: NavigateFunction = useNavigate();
  const dispatch = useDispatch();

  const [isCanEdit, setIsCanEdit] = useState<boolean>(false);

  const [formConsultation, setFormConsultation] = useState<{
    consultation_type_name: string;
  }>({
    consultation_type_name: "",
  });

  const handleChangeValue = (name: string, value: any) => {
    setFormConsultation((prev: any) => {
      return {
        ...prev,
        [name]: value,
      };
    });
  };

  useEffect(() => {
    if (action === "create") {
      setIsCanEdit(true);
    } else {
      setIsCanEdit(false);
    }
  }, [action]);

  useEffect(() => {
    if (data) {
      setFormConsultation({
        consultation_type_name: data.advisor_type_name,
      });
    }
  }, [data]);

  const handleSave = async (advisor_type_name?: any) => {
    if (advisor_type_name) {
      try {
        dispatch(unsetLoading());
        const response = await advisorTypeApi.createAdvisorType({
          advisor_type_name: advisor_type_name,
        });
        if (response.status < 300) {
          alertSuccess(
            "บันทึกประเภทคำปรึกษา",
            "บันทึกประเภทคำปรึกษา เรียบร้อยแล้ว",
            undefined,
            () => {
              onFetch && onFetch();
              setIsOpenModal(false);
              setFormConsultation({
                consultation_type_name: "",
              });
            }
          );
        }
      } catch (error) {
        alertError("บันทึกประเภทคำปรึกษา ไม่สำเร็จ");
      } finally {
        dispatch(unsetLoading());
      }
    }
  };

  const handleUpdate = async (id: number, advisor_type_name: string) => {
    if (id && advisor_type_name) {
      try {
        dispatch(unsetLoading());
        const response = await advisorTypeApi.updateAdvisorType(id, {
          advisor_type_name: advisor_type_name,
        });
        if (response.status < 300) {
          alertSuccess(
            "แก้ไขประเภทคำปรึกษา",
            "แก้ไขประเภทคำปรึกษา เรียบร้อยแล้ว",
            undefined,
            () => {
              onFetch && onFetch();
              setIsOpenModal(false);
              setIsCanEdit(false);
              setFormConsultation({
                consultation_type_name: "",
              });
            }
          );
        }
      } catch (error) {
        alertError("แก้ไขประเภทคำปรึกษา ไม่สำเร็จ");
      } finally {
        dispatch(unsetLoading());
      }
    }
  };

  const handleDelete = async (id: number) => {
    if (id) {
      alertActive(
        "ลบประเภทคำปรึกษา",
        "ท่านต้องการลบประเภทคำปรึกษา ใช่หรือไม่",
        undefined,
        undefined,
        async () => {
          dispatch(setLoading());
          await advisorTypeApi
            .deleteAdvisorType(+id)
            .then((res) => {
              if (res.status < 300) {
                alertSuccess(
                  "ลบประเภทคำปรึกษา เรียบร้อย",
                  undefined,
                  undefined,
                  () => {
                    onFetch && onFetch();
                    setIsOpenModal(false);
                    setIsCanEdit(false);
                  }
                );
              }
            })
            .catch(() => {
              alertError("ประเภทคำปรึกษา ไม่สำเร็จ");
            })
            .finally(() => {
              dispatch(unsetLoading());
            });
        }
      );
    }
    return;
  };

  return (
    <ModalCustom
      title={
        action === "create" ? "เพิ่มประเภทคำปรึกษา" : "แก้ไขประเภทคำปรึกษา"
      }
      subTitle="กรุณาระบุข้อมูลให้ครบถ้วนก่อนการยืนยัน"
      open={isOpenModal}
      onClose={() => {
        setIsOpenModal(false);
      }}
      component={
        <Grid container spacing={2}>
          <Grid
            item
            lg={12}
            md={12}
            sm={12}
            xs={12}
            sx={{
              alignSelf: "end",
            }}
          >
            <InputTextField
              heading={"ชื่อประเภท"}
              placeholder={"ชื่อประเภท"}
              value={formConsultation.consultation_type_name ?? ""}
              onchange={(e: React.ChangeEvent<HTMLInputElement>) => {
                handleChangeValue("consultation_type_name", e.target.value);
              }}
              disabled={!isCanEdit}
              required
              helperText={formConsultation.consultation_type_name === '' ? 'Consultation type is required.' : ''}
            />
          </Grid>

          <Grid
            item
            lg={12}
            md={12}
            sm={12}
            xs={12}
            sx={{
              display: "flex",
              justifyContent: "center",
            }}
          >
            <ButtonOutlined
              onClick={() => {
                setIsOpenModal(false);
                if (action !== "create"){
                  setIsCanEdit(false);
                }
              }}
              mode="del"
              buttonName="ยกเลิก"
              btnProps={{
                width: "125px",
                margin: "12px",
              }}
            />

            {isCanEdit && (
              <ButtonPrimary
                onClick={() => {
                  if (action === "create") {
                    handleSave(formConsultation.consultation_type_name);
                  } else if (action === "update") {
                    handleUpdate(
                      data ? data.id_advisor_type : null,
                      formConsultation.consultation_type_name
                    );
                  } else {
                    alertError("บันทึกเอกสาร ไม่สำเร็จ");
                  }
                }}
                buttonName={"บันทึก"}
                btnProps={{
                  width: "125px",
                  margin: "12px",
                }}
                mode="next"
              />
            )}

            {!isCanEdit && (
              <ButtonPrimary
                onClick={() => {
                  setIsCanEdit(true);
                }}
                buttonName={"แก้ไข"}
                btnProps={{
                  width: "125px",
                  margin: "12px",
                }}
              />
            )}

            {!isCanEdit && (
              <ButtonPrimary
                onClick={() => {
                  handleDelete(data ? data.id_advisor_type : null);
                }}
                mode="del"
                buttonName={"ลบ"}
                btnProps={{
                  width: "125px",
                  margin: "12px",
                }}
              />
            )}
          </Grid>
        </Grid>
      }
    />
  );
};

export default ModalFormConsultation;
